import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import { ib, type IbPayout, type IbRuleCreate } from "@/api/ib";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  FormError,
  Input,
  Label,
} from "@/components/ui";

function errMsg(e: unknown) {
  return e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e);
}

/**
 * Introducing-broker admin — commission rules, payout approval, and referral
 * attribution. Rules set the spread/commission split per account group;
 * payouts accrue per IB per period and are released through approve → mark-paid.
 */
export function AdminIbPage() {
  return (
    <div className="space-y-4">
      <h1 className="text-base font-semibold text-term-fg">Introducing brokers</h1>
      <RulesSection />
      <PayoutsSection />
      <ReferralsSection />
    </div>
  );
}

function RulesSection() {
  const rules = useQuery({ queryKey: ["ib-rules"], queryFn: () => ib.listRules() });
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();

  const toggle = useMutation({
    mutationFn: (r: { id: string; enabled: boolean }) => ib.patchRule(r.id, { enabled: !r.enabled }),
    onSuccess: () => {
      toast.push("Rule updated", "success");
      qc.invalidateQueries({ queryKey: ["ib-rules"] });
    },
    onError: (e) => toast.push(errMsg(e), "error"),
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Commission rules</span>
          <Button onClick={() => setOpen(true)}>New rule</Button>
        </div>
      </CardHeader>
      <CardBody className="p-0">
        {rules.isLoading ? (
          <div className="p-3 text-sm text-term-mute">Loading…</div>
        ) : !rules.data || rules.data.length === 0 ? (
          <EmptyState title="No rules" description="Define spread/commission splits paid to IBs per account group." />
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-term-elev text-term-mute">
              <tr>
                <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Account group</th>
                <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Spread %</th>
                <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Commission %</th>
                <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Flat/lot</th>
                <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Status</th>
                <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.data.map((r) => (
                <tr key={r.id} className="border-t border-term-border hover:bg-term-elev">
                  <td className="px-3 py-1.5 font-mono text-2xs text-term-dim">
                    {r.account_group_id ? r.account_group_id.slice(0, 8) : "all groups"}
                  </td>
                  <td className="px-3 py-1.5 text-right tnum text-term-fg">{r.spread_share_pct}</td>
                  <td className="px-3 py-1.5 text-right tnum text-term-fg">{r.commission_share_pct}</td>
                  <td className="px-3 py-1.5 text-right tnum text-term-fg">
                    {r.flat_per_lot} {r.currency}
                  </td>
                  <td className="px-3 py-1.5">
                    <Badge tone={r.enabled ? "good" : "neutral"}>{r.enabled ? "ON" : "OFF"}</Badge>
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    <Button variant="ghost" onClick={() => toggle.mutate(r)} disabled={toggle.isPending}>
                      {r.enabled ? "Disable" : "Enable"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardBody>
      {open && <RuleModal onClose={() => setOpen(false)} />}
    </Card>
  );
}

function RuleModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<IbRuleCreate>({
    spread_share_pct: "0",
    commission_share_pct: "0",
    flat_per_lot: "0",
    currency: "USD",
    enabled: true,
  });
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      ib.createRule({ ...form, account_group_id: form.account_group_id || null }),
    onSuccess: () => {
      toast.push("Rule created", "success");
      qc.invalidateQueries({ queryKey: ["ib-rules"] });
      onClose();
    },
    onError: (e) => setErr(errMsg(e)),
  });

  return (
    <Modal open onClose={onClose} title="New commission rule">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          create.mutate();
        }}
        className="space-y-3"
      >
        <div>
          <Label>Account group id (optional)</Label>
          <Input
            value={form.account_group_id ?? ""}
            onChange={(e) => setForm({ ...form, account_group_id: e.target.value || null })}
            placeholder="leave blank for all groups"
          />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label>Spread %</Label>
            <Input
              value={String(form.spread_share_pct)}
              onChange={(e) => setForm({ ...form, spread_share_pct: e.target.value })}
            />
          </div>
          <div>
            <Label>Commission %</Label>
            <Input
              value={String(form.commission_share_pct)}
              onChange={(e) => setForm({ ...form, commission_share_pct: e.target.value })}
            />
          </div>
          <div>
            <Label>Flat/lot</Label>
            <Input
              value={String(form.flat_per_lot)}
              onChange={(e) => setForm({ ...form, flat_per_lot: e.target.value })}
            />
          </div>
        </div>
        <FormError>{err}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={create.isPending}>
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function PayoutsSection() {
  const payouts = useQuery({
    queryKey: ["ib-payouts"],
    queryFn: () => ib.listPayouts(),
    refetchInterval: 30_000,
  });
  const [paying, setPaying] = useState<IbPayout | null>(null);
  const qc = useQueryClient();
  const toast = useToast();

  const approve = useMutation({
    mutationFn: (id: string) => ib.approvePayout(id),
    onSuccess: () => {
      toast.push("Payout approved", "success");
      qc.invalidateQueries({ queryKey: ["ib-payouts"] });
    },
    onError: (e) => toast.push(errMsg(e), "error"),
  });

  return (
    <Card>
      <CardHeader>
        <span className="text-sm font-medium">Payouts</span>
      </CardHeader>
      <CardBody className="p-0">
        {payouts.isLoading ? (
          <div className="p-3 text-sm text-term-mute">Loading…</div>
        ) : !payouts.data || payouts.data.length === 0 ? (
          <EmptyState title="No payouts" description="Accrued IB payouts appear here for approval." />
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-term-elev text-term-mute">
              <tr>
                <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">IB</th>
                <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Period</th>
                <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Amount</th>
                <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Lots</th>
                <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Status</th>
                <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {payouts.data.map((p) => (
                <tr key={p.id} className="border-t border-term-border hover:bg-term-elev">
                  <td className="px-3 py-1.5 font-mono text-2xs text-term-dim">
                    {p.ib_user_id.slice(0, 8)}
                  </td>
                  <td className="px-3 py-1.5 text-2xs text-term-dim">
                    {p.period_start ? new Date(p.period_start).toLocaleDateString() : "—"} ›{" "}
                    {p.period_end ? new Date(p.period_end).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-3 py-1.5 text-right tnum text-term-fg">
                    {p.amount} {p.currency}
                  </td>
                  <td className="px-3 py-1.5 text-right tnum text-term-dim">{p.referred_lot_volume}</td>
                  <td className="px-3 py-1.5">
                    <Badge
                      tone={p.status === "PAID" ? "good" : p.status === "APPROVED" ? "warn" : "neutral"}
                    >
                      {p.status}
                    </Badge>
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    <div className="flex justify-end gap-1">
                      {p.status === "PENDING" && (
                        <Button onClick={() => approve.mutate(p.id)} disabled={approve.isPending}>
                          Approve
                        </Button>
                      )}
                      {(p.status === "APPROVED" || p.status === "PENDING") && (
                        <Button variant="ghost" onClick={() => setPaying(p)}>
                          Mark paid
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardBody>
      {paying && <MarkPaidModal row={paying} onClose={() => setPaying(null)} />}
    </Card>
  );
}

function MarkPaidModal({ row, onClose }: { row: IbPayout; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [ref, setRef] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () => ib.markPayoutPaid(row.id, ref || undefined),
    onSuccess: () => {
      toast.push("Payout marked paid", "success");
      qc.invalidateQueries({ queryKey: ["ib-payouts"] });
      onClose();
    },
    onError: (e) => setErr(errMsg(e)),
  });

  return (
    <Modal open onClose={onClose} title="Mark payout paid">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          submit.mutate();
        }}
        className="space-y-3"
      >
        <div className="rounded-sm border border-term-border bg-term-elev px-2 py-1.5 text-xs text-term-dim">
          <span className="text-term-mute">Amount</span>{" "}
          <span className="font-mono text-term-fg">{row.amount} {row.currency}</span>
        </div>
        <div>
          <Label>Payment reference</Label>
          <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="wire / txn id" />
        </div>
        <FormError>{err}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={submit.isPending}>
            Mark paid
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function ReferralsSection() {
  const refs = useQuery({ queryKey: ["ib-referrals"], queryFn: () => ib.listReferrals() });

  return (
    <Card>
      <CardHeader>
        <span className="text-sm font-medium">Referrals</span>
      </CardHeader>
      <CardBody className="p-0">
        {refs.isLoading ? (
          <div className="p-3 text-sm text-term-mute">Loading…</div>
        ) : !refs.data || refs.data.length === 0 ? (
          <EmptyState title="No referrals" description="IB → client attributions appear here." />
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-term-elev text-term-mute">
              <tr>
                <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">IB (referrer)</th>
                <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Client (referred)</th>
                <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">When</th>
              </tr>
            </thead>
            <tbody>
              {refs.data.map((r) => (
                <tr key={r.id} className="border-t border-term-border hover:bg-term-elev">
                  <td className="px-3 py-1.5 font-mono text-2xs text-term-dim">
                    {r.referrer_user_id.slice(0, 8)}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-2xs text-term-dim">
                    {r.referred_user_id.slice(0, 8)}
                  </td>
                  <td className="px-3 py-1.5 text-2xs text-term-dim">
                    {r.referred_at ? new Date(r.referred_at).toLocaleString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
}
